import { useCallback, useEffect } from 'react';

import type { PromptOfTheDayWithFallbackStatus } from '@atlassian/rovo-prompt-of-the-day/use-prompt-of-the-day-with-fallback';

import {
	onPromptOfTheDayTaskFail,
	onPromptOfTheDayTaskSuccess,
} from '../../../common/utils/quick-find/events/prompt-of-the-day-operational';
import { useQuickFindAnalytics, useQuickFindAttributes } from '../analytics';

// Module state, so the event fires once per page load however often the loader mounts.
let hasFiredTaskFail = false;

export const resetPromptOfTheDayLoadAnalyticsForTests: () => void = () => {
	hasFiredTaskFail = false;
};

type Load = {
	status: PromptOfTheDayWithFallbackStatus;
	promptCount: number;
};

// `stale` and `unavailable` are the library's two outcomes of a failed regeneration. A stale
// entry whose prompts were all used leaves nothing on screen, so it reports no fallback.
export const usePromptOfTheDayLoadAnalytics: (load: Load) => void = ({ status, promptCount }) => {
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes } = useQuickFindAttributes();

	useEffect(() => {
		if ((status === 'stale' || status === 'unavailable') && !hasFiredTaskFail) {
			hasFiredTaskFail = true;
			fireAnalyticsEvent(
				onPromptOfTheDayTaskFail({
					attributes: commonAttributes,
					fallback: status === 'stale' && promptCount > 0 ? 'stale' : 'none',
				}),
			);
		}
	}, [status, promptCount, fireAnalyticsEvent, commonAttributes]);
};

// Pass as the library's `onGenerated`: one `taskSuccess` per successful generation, no page limit.
export const useReportPromptOfTheDayGenerated: () => (promptCount: number) => void = () => {
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes } = useQuickFindAttributes();

	return useCallback(
		(promptCount: number) => {
			fireAnalyticsEvent(
				onPromptOfTheDayTaskSuccess({ attributes: commonAttributes, promptCount }),
			);
		},
		[fireAnalyticsEvent, commonAttributes],
	);
};
